import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, Users, Target, AlertCircle, AlertTriangle, MessageSquare, ChevronDown, ChevronUp, Search, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import SearchableSelect from '../ui/SearchableSelect';
import api from '../../services/api';

console.log('SalesIntelligencePage module loaded');

type Tab = 'agencies' | 'pipeline' | 'groups';
type HealthFilter = '' | 'A_GRADE' | 'AT_RISK' | 'NURTURING' | 'DORMANT' | 'NEW';

interface Agency {
  agency_id: number;
  agency_name: string;
  total_quotations: number;
  converted: number;
  conversion_rate: number;
  avg_pax: number;
  total_reworks: number;
  avg_reworks: number;
  last_converted_at: string | null;
  days_since_conversion: number | null;
  last_quotation_at: string | null;
  days_since_quotation: number | null;
  quotations_last_30d: number;
  health: string;
}

interface PipelineItem {
  quotation_id: number;
  quotation_number: string;
  agency_name: string;
  status: string;
  total_pax: number;
  travel_date: string | null;
  created_at: string | null;
  remark_status: string | null;
  remark_text: string | null;
  itinerary_id: number;
}

interface GroupItem {
  itinerary_id: number;
  quotation_number: string | null;
  agency_name: string;
  total_pax: number;
  status: string;
  travel_date: string | null;
  created_at: string | null;
  price: number;
}

const HEALTH_COLORS: Record<string, string> = {
  A_GRADE: 'bg-green-100 text-green-800',
  AT_RISK: 'bg-red-100 text-red-800',
  NURTURING: 'bg-blue-100 text-blue-800',
  DORMANT: 'bg-orange-100 text-orange-800',
  NEW: 'bg-purple-100 text-purple-800'
};

const STATUS_COLORS: Record<string, string> = {
  CONVERTED: 'bg-green-100 text-green-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  DRAFT: 'bg-gray-100 text-gray-800',
  CANCELED: 'bg-red-100 text-red-800',
  PENDING_CANCELLATION: 'bg-red-100 text-red-700'
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const SalesIntelligencePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('agencies');
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [travelDateFrom, setTravelDateFrom] = useState('');
  const [travelDateTo, setTravelDateTo] = useState('');
  const [healthFilter, setHealthFilter] = useState<HealthFilter>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [agencySearch, setAgencySearch] = useState('');
  const [agencyOptions, setAgencyOptions] = useState<{value: string | number; label: string}[]>([]);
  const [selectedAgency, setSelectedAgency] = useState<string | number>('');
  const [minPax, setMinPax] = useState('20');
  const [maxPax, setMaxPax] = useState('');

  // Agency detail modal
  const [showAgencyModal, setShowAgencyModal] = useState(false);
  const [agencyDetail, setAgencyDetail] = useState<any>(null);
  const [agencyMonthly, setAgencyMonthly] = useState<any[]>([]);

  // Data
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [agencySummary, setAgencySummary] = useState<any>(null);
  const [pipeline, setPipeline] = useState<PipelineItem[]>([]);
  const [pipelineSummary, setPipelineSummary] = useState<any>(null);
  const [groups, setGroups] = useState<GroupItem[]>([]);
  const [groupsSummary, setGroupsSummary] = useState<any>(null);

  // Remark editing
  const [editingRemark, setEditingRemark] = useState<number | null>(null);
  const [remarkText, setRemarkText] = useState('');
  const [remarkStatus, setRemarkStatus] = useState('PENDING');

  // Sort
  const [sortField, setSortField] = useState<string>('conversion_rate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadAgencies();
    loadData();
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadAgencies = async () => {
    try {
      const res = await api.get('/role-management/agencies');
      if (res.data.success) {
        setAgencyOptions([
          { value: '', label: 'All Agencies' },
          ...res.data.data.map((a: any) => ({ value: a.agency_id, label: a.agency_name }))
        ]);
      }
    } catch (err) {
      console.error('Error loading agencies:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (travelDateFrom) params.travel_date_from = travelDateFrom;
      if (travelDateTo) params.travel_date_to = travelDateTo;

      if (activeTab === 'agencies') {
        if (healthFilter) params.health_filter = healthFilter;
        if (selectedAgency) params.agency_search = agencyOptions.find(a => a.value === selectedAgency)?.label || '';
        const res = await api.get('/sales-intelligence/agencies', { params });
        setAgencies(res.data.data);
        setAgencySummary(res.data.summary);
      } else if (activeTab === 'pipeline') {
        if (selectedAgency) params.agency_id = selectedAgency;
        if (statusFilter) params.status = statusFilter;
        const res = await api.get('/sales-intelligence/pipeline', { params });
        setPipeline(res.data.data);
        setPipelineSummary(res.data.summary);
      } else {
        if (selectedAgency) params.agency_id = selectedAgency;
        if (minPax) params.min_pax = parseInt(minPax);
        if (maxPax) params.max_pax = parseInt(maxPax);
        if (statusFilter) params.status = statusFilter;
        const res = await api.get('/sales-intelligence/groups', { params });
        setGroups(res.data.data);
        setGroupsSummary(res.data.summary);
      }
    } catch (err) {
      console.error('Error loading sales data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRemark = async (quotationId: number) => {
    try {
      await api.put(`/sales-intelligence/remark/${quotationId}`, {
        remark_status: remarkStatus,
        remark_text: remarkText
      });
      setEditingRemark(null);
      loadData();
    } catch (err) {
      console.error('Error saving remark:', err);
    }
  };

  const sortedAgencies = useMemo(() => {
    return [...agencies].sort((a: any, b: any) => {
      const aVal = a[sortField] ?? 0;
      const bVal = b[sortField] ?? 0;
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(String(bVal)) : String(bVal).localeCompare(aVal);
      return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [agencies, sortField, sortDir]);

  const sortedPipeline = useMemo(() => {
    return [...pipeline].sort((a: any, b: any) => {
      const aVal = a[sortField] ?? '';
      const bVal = b[sortField] ?? '';
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(String(bVal)) : String(bVal).localeCompare(aVal);
      return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [pipeline, sortField, sortDir]);

  const sortedGroups = useMemo(() => {
    return [...groups].sort((a: any, b: any) => {
      const aVal = a[sortField] ?? 0;
      const bVal = b[sortField] ?? 0;
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(String(bVal)) : String(bVal).localeCompare(aVal);
      return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [groups, sortField, sortDir]);

  const openAgencyDetail = async (agency: Agency) => {
    setAgencyDetail(agency);
    setShowAgencyModal(true);
    try {
      const res = await api.get(`/sales-intelligence/agency-monthly/${agency.agency_id}`);
      if (res.data.success) setAgencyMonthly(res.data.data);
    } catch (err) {
      console.error('Error loading agency monthly:', err);
      setAgencyMonthly([]);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  return (
    <div className="p-3 sm:p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-blue-600" />
            Sales Intelligence
          </h1>
          <p className="text-sm text-gray-500 mt-1">Agency health, pipeline tracking & group identification</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-6 flex flex-wrap gap-3 sm:gap-4 items-end">
        <div className="w-full sm:w-auto">
          <label className="text-xs font-medium text-gray-500 block mb-1">Created From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
        </div>
        <div className="w-full sm:w-auto">
          <label className="text-xs font-medium text-gray-500 block mb-1">Created To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
        </div>
        <div className="w-full sm:w-auto">
          <label className="text-xs font-medium text-gray-500 block mb-1">Travel From</label>
          <input type="date" value={travelDateFrom} onChange={e => setTravelDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
        </div>
        <div className="w-full sm:w-auto">
          <label className="text-xs font-medium text-gray-500 block mb-1">Travel To</label>
          <input type="date" value={travelDateTo} onChange={e => setTravelDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
        </div>
        {activeTab === 'agencies' && (
          <>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Health</label>
              <select value={healthFilter} onChange={e => setHealthFilter(e.target.value as HealthFilter)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All</option>
                <option value="A_GRADE">A-Grade</option>
                <option value="AT_RISK">At Risk</option>
                <option value="NURTURING">Nurturing</option>
                <option value="DORMANT">Dormant</option>
                <option value="NEW">New</option>
              </select>
            </div>
          </>
        )}
        <div className="min-w-[250px] w-full sm:w-auto">
          <label className="text-xs font-medium text-gray-500 block mb-1">Agency</label>
          <SearchableSelect
            options={agencyOptions}
            value={selectedAgency}
            onChange={(val) => setSelectedAgency(val)}
            placeholder="All Agencies"
          />
        </div>
        {(activeTab === 'pipeline') && (
          <div>
            <label className="text-xs font-medium text-gray-500 block mb-1">Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">All</option>
              <option value="CONVERTED">Converted</option>
              <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
              <option value="TENTATIVE">Tentative</option>
              <option value="CANCELED">Canceled</option>
              <option value="PENDING_CANCELLATION">Pending Cancellation</option>
            </select>
          </div>
        )}
        {activeTab !== 'groups' && (
          <div className="flex items-end">
            <button onClick={() => loadData()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              Search
            </button>
          </div>
        )}
      </div>
      {activeTab === 'groups' && (
        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-6 flex flex-wrap gap-3 sm:gap-4 items-end -mt-4 border-t-0 rounded-t-none">
          <div>
            <label className="text-xs font-medium text-gray-500 block mb-1">Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">All</option>
              <option value="CONVERTED">Converted</option>
              <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
              <option value="TENTATIVE">Tentative</option>
              <option value="CANCELED">Canceled</option>
              <option value="PENDING_CANCELLATION">Pending Cancellation</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 block mb-1">Min PAX</label>
            <input type="number" value={minPax} onChange={e => setMinPax(e.target.value)}
              placeholder="6" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-20" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 block mb-1">Max PAX</label>
            <input type="number" value={maxPax} onChange={e => setMaxPax(e.target.value)}
              placeholder="No limit" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-20" />
          </div>
          <div className="flex items-end">
            <button onClick={() => loadData()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              Search
            </button>
          </div>
        </div>
      )}
      {activeTab === 'agencies' && agencySummary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 sm:gap-4 mb-6">
          <SummaryCard label="Total Agencies" value={agencySummary.total_agencies} icon={<Users className="w-5 h-5 text-blue-600" />} />
          <SummaryCard label="A-Grade" value={agencySummary.health_counts?.A_GRADE || 0} icon={<Target className="w-5 h-5 text-green-600" />} color="text-green-700" />
          <SummaryCard label="At Risk" value={agencySummary.health_counts?.AT_RISK || 0} icon={<AlertTriangle className="w-5 h-5 text-red-600" />} color="text-red-700" />
          <SummaryCard label="Nurturing" value={agencySummary.health_counts?.NURTURING || 0} icon={<TrendingUp className="w-5 h-5 text-blue-600" />} color="text-blue-700" />
          <SummaryCard label="Dormant" value={agencySummary.health_counts?.DORMANT || 0} icon={<AlertCircle className="w-5 h-5 text-orange-600" />} color="text-orange-700" />
          <SummaryCard label="Avg Conversion" value={`${agencySummary.avg_conversion_rate}%`} icon={<TrendingUp className="w-5 h-5 text-purple-600" />} color="text-purple-700" />
        </div>
      )}
      {activeTab === 'pipeline' && pipelineSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard label="Total Quotations" value={pipelineSummary.total} icon={<Users className="w-5 h-5 text-blue-600" />} />
          {Object.entries(pipelineSummary.by_status || {}).slice(0, 3).map(([status, count]) => (
            <SummaryCard key={status} label={status} value={count as number} icon={<Target className="w-5 h-5 text-gray-600" />} />
          ))}
        </div>
      )}
      {activeTab === 'groups' && groupsSummary && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <SummaryCard label="Group Requests" value={groupsSummary.total_groups} icon={<Users className="w-5 h-5 text-blue-600" />} />
          <SummaryCard label="Total PAX" value={groupsSummary.total_pax} icon={<Users className="w-5 h-5 text-green-600" />} />
          <SummaryCard label="Converted" value={groupsSummary.converted} icon={<Target className="w-5 h-5 text-green-600" />} color="text-green-700" />
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 flex overflow-x-auto">
          {(['agencies', 'pipeline', 'groups'] as Tab[]).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 sm:px-6 py-3 text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                activeTab === tab ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {tab === 'agencies' ? 'Agency Health' : tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden p-2 space-y-2">
                {activeTab === 'agencies' && sortedAgencies.map(a => (
                  <div key={a.agency_id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm" onClick={() => openAgencyDetail(a)}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-blue-600 text-sm truncate flex-1">{a.agency_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ml-2 ${HEALTH_COLORS[a.health] || 'bg-gray-100'}`}>
                        {a.health.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div><span className="text-gray-500">Quotes:</span> <span className="font-medium">{a.total_quotations}</span></div>
                      <div><span className="text-gray-500">Conv:</span> <span className="font-medium">{a.converted}</span></div>
                      <div><span className="text-gray-500">Rate:</span> <span className="font-medium">{a.conversion_rate}%</span></div>
                      <div><span className="text-gray-500">PAX:</span> <span className="font-medium">{a.avg_pax}</span></div>
                      <div><span className="text-gray-500">30d:</span> <span className={`font-medium ${a.quotations_last_30d > 0 ? 'text-green-700' : 'text-gray-400'}`}>{a.quotations_last_30d}</span></div>
                      <div><span className="text-gray-500">Last:</span> <span className="font-medium">{a.days_since_quotation != null ? `${a.days_since_quotation}d` : '—'}</span></div>
                    </div>
                  </div>
                ))}
                {activeTab === 'pipeline' && sortedPipeline.map(item => (
                  <div key={item.quotation_id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-blue-600 text-sm">{item.quotation_number}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[item.status] || 'bg-gray-100'}`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-gray-500">Agency:</span> <span className="font-medium">{item.agency_name}</span></div>
                      <div><span className="text-gray-500">PAX:</span> <span className="font-medium">{item.total_pax}</span></div>
                      <div><span className="text-gray-500">Travel:</span> <span className="font-medium">{formatDate(item.travel_date)}</span></div>
                      <div><span className="text-gray-500">Remark:</span> <span className="font-medium">{item.remark_status || '—'}</span></div>
                    </div>
                  </div>
                ))}
                {activeTab === 'groups' && sortedGroups.map(g => (
                  <div key={g.itinerary_id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{g.quotation_number || `WH_${g.itinerary_id}`}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[g.status] || 'bg-gray-100'}`}>
                        {g.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-gray-500">Agency:</span> <span className="font-medium">{g.agency_name}</span></div>
                      <div><span className="text-gray-500">PAX:</span> <span className="font-semibold">{g.total_pax}</span></div>
                      <div><span className="text-gray-500">Travel:</span> <span className="font-medium">{formatDate(g.travel_date)}</span></div>
                      <div><span className="text-gray-500">Price:</span> <span className="font-medium">{g.price > 0 ? `$${g.price.toLocaleString()}` : '—'}</span></div>
                    </div>
                  </div>
                ))}
                {((activeTab === 'agencies' && sortedAgencies.length === 0) || (activeTab === 'pipeline' && sortedPipeline.length === 0) || (activeTab === 'groups' && sortedGroups.length === 0)) && (
                  <div className="py-8 text-center text-gray-500 text-sm">No data found</div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
              {activeTab === 'agencies' ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Agency</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('health')}>
                    <span className="flex items-center gap-1">
                      Health <SortIcon field="health" />
                      <span className="relative group ml-1">
                        <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                        <span className="absolute left-0 top-5 z-50 hidden group-hover:block w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg leading-relaxed">
                          <b>A-Grade:</b> Conv ≥20%, active in last 30d, converted in last 90d<br/>
                          <b>At Risk:</b> Current silence is a statistical anomaly vs their normal enquiry pattern<br/>
                          <b>Nurturing:</b> Still engaging or decent conversion, but not top tier<br/>
                          <b>Dormant:</b> No recent activity, low conversion<br/>
                          <b>New:</b> ≤2 quotations, too early to classify
                        </span>
                      </span>
                    </span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('total_quotations')}>
                    <span className="flex items-center justify-end gap-1">Quotations <SortIcon field="total_quotations" /></span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('converted')}>
                    <span className="flex items-center justify-end gap-1">Converted <SortIcon field="converted" /></span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('conversion_rate')}>
                    <span className="flex items-center justify-end gap-1">Conv % <SortIcon field="conversion_rate" /></span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('avg_pax')}>
                    <span className="flex items-center justify-end gap-1">Avg PAX <SortIcon field="avg_pax" /></span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('quotations_last_30d')}>
                    <span className="flex items-center justify-end gap-1">Last 30d <SortIcon field="quotations_last_30d" /></span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('days_since_quotation')}>
                    <span className="flex items-center justify-end gap-1">Last Enquiry <SortIcon field="days_since_quotation" /></span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Last Converted</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-600">Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedAgencies.map(a => (
                  <tr key={a.agency_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-blue-600 cursor-pointer hover:underline" onClick={() => openAgencyDetail(a)}>{a.agency_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${HEALTH_COLORS[a.health] || 'bg-gray-100'}`}>
                        {a.health.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">{a.total_quotations}</td>
                    <td className="px-4 py-3 text-right">{a.converted}</td>
                    <td className="px-4 py-3 text-right font-medium">{a.conversion_rate}%</td>
                    <td className="px-4 py-3 text-right">{a.avg_pax}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-medium ${a.quotations_last_30d > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                        {a.quotations_last_30d}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">
                      {a.days_since_quotation != null ? `${a.days_since_quotation}d ago` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">
                      {a.days_since_conversion != null ? `${a.days_since_conversion}d ago` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <div className="w-16 h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              a.conversion_rate >= 25 ? 'bg-green-500' : a.conversion_rate >= 10 ? 'bg-blue-500' : 'bg-orange-400'
                            }`}
                            style={{ width: `${Math.min(a.conversion_rate, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 w-8">{a.conversion_rate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedAgencies.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">No agencies found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'pipeline' ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('quotation_number')}>
                    <span className="flex items-center gap-1">Quotation <SortIcon field="quotation_number" /></span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('agency_name')}>
                    <span className="flex items-center gap-1">Agency <SortIcon field="agency_name" /></span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('status')}>
                    <span className="flex items-center gap-1">Status <SortIcon field="status" /></span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('total_pax')}>
                    <span className="flex items-center justify-end gap-1">PAX <SortIcon field="total_pax" /></span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('travel_date')}>
                    <span className="flex items-center gap-1">Travel Date <SortIcon field="travel_date" /></span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Remark</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedPipeline.map(item => (
                  <tr key={item.quotation_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-blue-600">{item.quotation_number}</td>
                    <td className="px-4 py-3">{item.agency_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[item.status] || 'bg-gray-100'}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">{item.total_pax}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(item.travel_date)}</td>
                    <td className="px-4 py-3">
                      {editingRemark === item.quotation_id ? (
                        <div className="flex flex-col gap-1">
                          <select value={remarkStatus} onChange={e => setRemarkStatus(e.target.value)}
                            className="border rounded px-2 py-1 text-xs">
                            <option value="PENDING">Pending</option>
                            <option value="FOLLOWED_UP">Followed Up</option>
                            <option value="HOT_LEAD">Hot Lead</option>
                            <option value="LOST">Lost</option>
                          </select>
                          <input value={remarkText} onChange={e => setRemarkText(e.target.value)}
                            placeholder="Note..." className="border rounded px-2 py-1 text-xs" />
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">
                          {item.remark_status ? `${item.remark_status}${item.remark_text ? ': ' + item.remark_text : ''}` : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingRemark === item.quotation_id ? (
                        <div className="flex gap-1">
                          <button onClick={() => handleSaveRemark(item.quotation_id)}
                            className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Save</button>
                          <button onClick={() => setEditingRemark(null)}
                            className="px-2 py-1 bg-gray-200 rounded text-xs">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => {
                          setEditingRemark(item.quotation_id);
                          setRemarkStatus(item.remark_status || 'PENDING');
                          setRemarkText(item.remark_text || '');
                        }} className="text-blue-600 hover:text-blue-800">
                          <MessageSquare className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {sortedPipeline.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No pipeline data</td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('itinerary_id')}>
                    <span className="flex items-center gap-1">ID <SortIcon field="itinerary_id" /></span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('quotation_number')}>
                    <span className="flex items-center gap-1">Quotation <SortIcon field="quotation_number" /></span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('agency_name')}>
                    <span className="flex items-center gap-1">Agency <SortIcon field="agency_name" /></span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('total_pax')}>
                    <span className="flex items-center justify-end gap-1">PAX <SortIcon field="total_pax" /></span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('status')}>
                    <span className="flex items-center gap-1">Status <SortIcon field="status" /></span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer" onClick={() => handleSort('travel_date')}>
                    <span className="flex items-center gap-1">Travel Date <SortIcon field="travel_date" /></span>
                  </th>

                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedGroups.map(g => (
                  <tr key={g.itinerary_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">WH_{g.itinerary_id}</td>
                    <td className="px-4 py-3 text-blue-600">{g.quotation_number || '—'}</td>
                    <td className="px-4 py-3">{g.agency_name}</td>
                    <td className="px-4 py-3 text-right font-semibold">{g.total_pax}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[g.status] || 'bg-gray-100'}`}>
                        {g.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(g.travel_date)}</td>
                  </tr>
                ))}
                {sortedGroups.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No group requests found</td></tr>
                )}
              </tbody>
            </table>
          )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Agency Detail Modal */}
      {showAgencyModal && agencyDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setShowAgencyModal(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-3xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{agencyDetail.agency_name}</h2>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${HEALTH_COLORS[agencyDetail.health] || 'bg-gray-100'}`}>
                  {agencyDetail.health.replace('_', ' ')}
                </span>
                {agencyDetail.health_reason && (
                  <p className="mt-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    {agencyDetail.health_reason}
                  </p>
                )}
              </div>
              <button onClick={() => setShowAgencyModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            {/* Metric Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 p-4 sm:p-6 pb-2">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Quotations</p>
                <p className="text-lg font-bold text-blue-700">{agencyDetail.total_quotations}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Converted</p>
                <p className="text-lg font-bold text-green-700">{agencyDetail.converted}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Conv Rate</p>
                <p className="text-lg font-bold text-purple-700">{agencyDetail.conversion_rate}%</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Avg PAX</p>
                <p className="text-lg font-bold text-orange-700">{agencyDetail.avg_pax}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-500">Last 30d</p>
                <p className="text-lg font-bold text-gray-700">{agencyDetail.quotations_last_30d}</p>
              </div>
            </div>

            {/* Monthly Chart */}
            <div className="p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Monthly Trend (Last 6 Months)</h3>
              {agencyMonthly.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={agencyMonthly} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="quotations" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} name="Quotations" />
                    <Line type="monotone" dataKey="converted" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} name="Converted" />
                    <Line type="monotone" dataKey="packages" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} name="Packages (Travel Date)" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center text-gray-400 py-8">Loading...</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, icon, color }: { label: string; value: string | number; icon: React.ReactNode; color?: string }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <span className="text-xs font-medium text-gray-500">{label}</span>
    </div>
    <p className={`text-xl font-bold ${color || 'text-gray-900'}`}>{value}</p>
  </div>
);

export default SalesIntelligencePage;
